import Api from '../axios/api';
import { Geo, Address } from '../interfaces/geo.interface';

export interface RequestLocations {
	phrase: string;
	countryCode?: string;
	maxResults: string;
}

export interface RequestLocationDetails {
	locationid: string;
}
export interface LocationDetails {
	Latitude: number;
	Longitude: number;
	Country: string;
	address?: Address;
}

export interface GetLocationsResponse {
	data: Geo[];
}
export interface GetLocationDetailsResponse {
	data: LocationDetails;
}

interface RouteParams {
	points: string[];
	transportMode?: 'truck' | 'car';
	alternatives?: number;
	height?: number;
	weight?: number;
	width?: number;
	length?: number;
	axleWeight?: number;
	trailerCount?: number;
}

export interface RouteResponse {
	shape: string;
	distance: number;
	trafficTime: number;
}

export interface ResponnseRouteResponse {
	data: RouteResponse[];
}

export const toRouteDto = (
	response: ResponnseRouteResponse | undefined,
	routeIndex = 0,
): RouteResponse | undefined => {
	const route = response?.data?.[routeIndex];
	if (!route?.shape) return undefined;
	return {
		shape: route.shape,
		distance: route.distance || 0,
		trafficTime: route.trafficTime || 0,
	};
};

interface QueryParamsAddresses {
	search?: string;
	limit?: number;
	offset?: number;
}

export interface ReverseGeocodeParams {
	lat: string;
	lng: string;
	countryCode?: string;
}

export interface ReverseGeocodeAddress {
	label: string;
	countryCode: string;
	countryName: string;
	state: string;
	county: string;
	city: string;
	street: string;
	postalCode: string;
	address?: string;
}

export interface ReverseGeocodeItem {
	title: string;
	id: string;
	resultType: string;
	address: ReverseGeocodeAddress;
	position: { lat: number; lng: number };
	distance: number;
	mapView: {
		west: number;
		south: number;
		east: number;
		north: number;
	};
}

export interface GetReverseGeocodeResponse {
	data: {
		items: ReverseGeocodeItem[];
	};
}

export interface AddressesI {
	lat: number;
	long: number;
	country: string;
	address: string;
}

export interface GetAddressesResponse {
	data: { items: AddressesI[]; count: number };
}
export class GeoService {
	static getLocations(query: RequestLocations): Promise<GetLocationsResponse> {
		const params = new URLSearchParams({
			maxResults: query.maxResults,
			phrase: query.phrase,
			...(query.countryCode && { countryCode: query.countryCode }),
		});
		return Api.get(`/v1/customer/geo/suggestions?${params}`);
	}

	static async getDataLocations(query: RequestLocations): Promise<Geo[]> {
		const params = new URLSearchParams({
			maxResults: query.maxResults,
			phrase: query.phrase,
		});
		const { data } = await Api.get(`/v1/customer/geo/suggestions?${params}`);
		return data;
	}

	static getLocationDetails(
		query: RequestLocationDetails,
	): Promise<GetLocationDetailsResponse> {
		const params = new URLSearchParams({
			locationid: query.locationid,
		});
		return Api.get(`/v1/customer/geo/location?${params}`);
	}

	static getRoute(query: RouteParams): Promise<ResponnseRouteResponse> {
		return Api.get('/v1/customer/geo/route', {
			params: query,
		});
	}

	static getAddresses(
		params: QueryParamsAddresses,
		target = 'producer',
	): Promise<GetAddressesResponse> {
		return Api.get(`/v1/${target}/addresses`, { params });
	}

	static reverseGeocode(
		query: ReverseGeocodeParams,
	): Promise<GetReverseGeocodeResponse> {
		return Api.get('/v1/customer/geo/reverse-geocode', {
			params: query,
		});
	}

	static getAutosuggest(
		query: RequestLocations,
	): Promise<GetLocationsResponse> {
		const params = new URLSearchParams({
			maxResults: query.maxResults,
			phrase: query.phrase,
			...(query.countryCode && { countryCode: query.countryCode }),
		});
		return Api.get(`/v1/customer/geo/autosuggest?${params}`);
	}
}
